import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Search, ShoppingCart } from 'lucide-react'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import { Progress } from '@/common/ui/progress'
import { Skeleton } from '@/common/ui/skeleton'
import { formatUsd } from '@/common/lib/utils'
import useQueryGetDomainQuota from '../../api/hooks/useQueryGetDomainQuota'
import useQuerySearchDomains from '../../api/hooks/useQuerySearchDomains'
import ResultRow from './ResultRow'

const formSchema = z.object({
	query: z.string().min(1, 'กรุณากรอกชื่อโดเมนที่ต้องการค้นหา'),
})

type FormValues = z.infer<typeof formSchema>

export default function BuyDomainDialog() {
	const [open, setOpen] = useState(false)
	const [submittedQuery, setSubmittedQuery] = useState('')

	const { data: quota, isLoading: isQuotaLoading } =
		useQueryGetDomainQuota(open)

	const {
		data: searchData,
		isFetching: isSearching,
		isError: isSearchError,
	} = useQuerySearchDomains(
		{ query: submittedQuery },
		open && submittedQuery.length > 0,
	)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: { query: '' },
	})

	const onSubmit = (values: FormValues) => {
		setSubmittedQuery(values.query.trim().toLowerCase())
	}

	const handleOpenChange = (next: boolean) => {
		setOpen(next)
		if (!next) {
			setSubmittedQuery('')
			form.reset()
		}
	}

	const remainingUsd = quota?.remainingUsd ?? 0
	const markupUsd = quota?.markupUsd ?? 0
	const capUsd = quota?.capUsd ?? 0
	const spentUsd = quota?.spentUsd ?? 0
	const spentPct = capUsd > 0 ? Math.min((spentUsd / capUsd) * 100, 100) : 0
	const results = searchData?.results ?? []

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant="outline">
					<ShoppingCart className="mr-2 h-4 w-4" />
					ซื้อโดเมน
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>ซื้อโดเมนใหม่</DialogTitle>
					<DialogDescription>
						ค้นหาและซื้อโดเมนใหม่ ราคาที่แสดงรวมค่าบริการแล้ว
						และจะเชื่อมต่อกับเว็บไซต์ของคุณโดยอัตโนมัติ
					</DialogDescription>
				</DialogHeader>

				{/* Quota summary */}
				<div className="rounded-md border bg-muted/40 p-3">
					{isQuotaLoading ? (
						<div className="space-y-2">
							<Skeleton className="h-4 w-40" />
							<Skeleton className="h-2 w-full" />
							<Skeleton className="h-3 w-56" />
						</div>
					) : (
						<div className="space-y-2">
							<div className="flex items-center justify-between text-sm">
								<span className="text-muted-foreground">
									งบประมาณเดือนนี้{quota ? ` (${quota.billingMonth})` : ''}
								</span>
								<span className="font-semibold">
									คงเหลือ {formatUsd(remainingUsd)}
								</span>
							</div>
							<Progress value={spentPct} />
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>
									ใช้ไป {formatUsd(spentUsd)} จาก {formatUsd(capUsd)}
								</span>
								<span>+{formatUsd(markupUsd)} ต่อโดเมน</span>
							</div>
						</div>
					)}
				</div>

				{/* Search form */}
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex items-start gap-2"
					>
						<FormField
							control={form.control}
							name="query"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormControl>
										<Input
											placeholder="เช่น mybrand หรือ mybrand.com"
											{...field}
											disabled={isSearching}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" disabled={isSearching}>
							{isSearching ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Search className="h-4 w-4" />
							)}
							<span className="ml-2">ค้นหา</span>
						</Button>
					</form>
				</Form>

				{/* Results */}
				<div className="max-h-[320px] space-y-2 overflow-y-auto">
					{isSearching ? (
						[1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-14 w-full rounded-md" />
						))
					) : isSearchError ? (
						<p className="py-6 text-center text-sm text-destructive">
							ค้นหาโดเมนล้มเหลว กรุณาลองใหม่อีกครั้ง
						</p>
					) : submittedQuery && results.length === 0 ? (
						<p className="py-6 text-center text-sm text-muted-foreground">
							ไม่พบโดเมนที่ตรงกับการค้นหา
						</p>
					) : (
						results.map((item) => (
							<ResultRow
								key={item.domain}
								item={item}
								remainingUsd={remainingUsd}
								markupUsd={markupUsd}
							/>
						))
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
